import { useInternationalization } from '@progress/kendo-react-intl';
import { useMemo } from 'react';
import ModalContentPreference from 'src/components/ModalContentPreference';
import NumberInput from 'src/components/NumberInput';
import PopUp from 'src/components/design-system/PopUp';
import { getBgColor, getIcon } from 'src/utils/icons';
import { useContextSelector } from 'use-context-selector';
import { InvoiceContext } from '../InvoiceDetailsContext';
import OcrIndicator from '../split_line_item/OcrIndicator';

const Shipping = () => {
  const invoice = useContextSelector(InvoiceContext, (value) => value.invoice);
  const handleInputChange = useContextSelector(InvoiceContext, (value) => value.handleInputChange);
  const ocrResponse = useContextSelector(InvoiceContext, (value) => value.ocrResponse);
  const initialInvoice = useContextSelector(InvoiceContext, (value) => value.initialInvoice);
  const handleHoverOcrField = useContextSelector(
    InvoiceContext,
    (value) => value.handleHoverOcrField
  );
  const warnings = useContextSelector(InvoiceContext, (value) => value.warnings);
  const isInvoiceEditDisabled = useContextSelector(
    InvoiceContext,
    (value) => value.isInvoiceEditDisabled
  );
  const errors = useContextSelector(InvoiceContext, (value) => value.errors);
  const hasWarning = useMemo(() => !!warnings.shipCostAmt.length, [warnings.shipCostAmt.length]);
  const hasError = useMemo(() => !!errors.shipCostAmt.length, [errors.shipCostAmt.length]);
  const intl = useInternationalization();

  const isEdited = useMemo(
    () => Number(ocrResponse.totalShipping?.value || 0) !== Number(invoice?.shipCostAmt || 0),
    [invoice?.shipCostAmt, ocrResponse.totalShipping?.value]
  );

  const showOcrIndicator = useMemo(() => {
    const confidence = ocrResponse?.totalShipping?.confidence;
    const hasMeaningfulConfidence =
      confidence != null && confidence !== '' && parseFloat(confidence) !== 0;
    return Boolean(
      initialInvoice?.ocrGenerated &&
        hasMeaningfulConfidence &&
        Number(invoice?.shipCostAmt) !== 0
    );
  }, [initialInvoice?.ocrGenerated, ocrResponse?.totalShipping?.confidence, invoice?.shipCostAmt]);

  return (
    <PopUp
      content={
        <ModalContentPreference
          hasWarning={hasWarning}
          hasError={hasError}
          warningContent={warnings.shipCostAmt}
          errorContent={errors.shipCostAmt}
        />
      }
      show={hasError || hasWarning}
      popupAlign={{ horizontal: 'center', vertical: 'bottom' }}
      anchorAlign={{ horizontal: 'right', vertical: 'top' }}
    >
      <div className={`p-1 ${getBgColor(errors.shipCostAmt, warnings.shipCostAmt)} text-[13px]`}>
        <div className="flex justify-between max-w-[177px] flex-grow">
          <p className="font-bold flex items-center pb-1">
            Shipping
            <span className="px-2">{getIcon(errors.shipCostAmt, warnings.shipCostAmt)}</span>
          </p>
          {showOcrIndicator && (
            <OcrIndicator
              isEdited={isEdited}
              ocrConfidence={ocrResponse.totalShipping?.confidence ?? ''}
              onHover={() => handleHoverOcrField('totalShipping')}
            />
          )}
        </div>
        {isInvoiceEditDisabled ? (
          <p title={invoice?.shipCostAmt?.toString() || ''}>
            {intl.format('{0:c2}', Number(invoice?.shipCostAmt) || 0)}
          </p>
        ) : (
          <NumberInput
            title={invoice?.shipCostAmt?.toString() || ''}
            value={invoice?.shipCostAmt.toString()}
            onChange={handleInputChange('shipCostAmt')}
            prefix="$"
            valid={!(hasWarning || hasError)}
            onFocus={() => handleHoverOcrField('totalShipping')}
            ocr-data="totalShipping"
          />
        )}
      </div>
    </PopUp>
  );
};

export default Shipping;
